import { statusOf } from '../../esign/EsignPanel';
import { householdPosition } from '../assessment/financials';

// Primary tabs → sub-sections. Sub-section ids keep the legacy tab names so ?tab= links and onGo() calls still work.
export const PROFILE_TABS = [
  { key: 'overview', label: 'Overview', subs: ['Overview'] },
  { key: 'client', label: 'Client information', subs: ['Applicants', 'Fact find', 'Financials'] },
  { key: 'documents', label: 'Documents', subs: ['Documents', 'Exceptions'] },
  { key: 'compliance', label: 'E-Sign & ID', subs: ['E-Sign', 'ID Verification', 'Disclosures'] },
  { key: 'team', label: 'Team members', subs: ['Team'] },
  { key: 'activity', label: 'Notes & activity', subs: ['Notes', 'Activity'] },
  { key: 'insights', label: 'AI insights', subs: ['AI Readiness', 'Meeting Intelligence'] },
];
export const ALL_SUBS = PROFILE_TABS.flatMap((t) => t.subs);
export const primaryOf = (sub) => PROFILE_TABS.find((t) => t.subs.includes(sub)) || PROFILE_TABS[0];

const PURPOSE = { first_home: 'First home purchase', purchase: 'Home purchase', investment: 'Investment purchase', land: 'Land purchase', construction: 'Build / renovate', refinance: 'Refinance', debt_consolidation: 'Debt consolidation', debt_restructure: 'Debt restructure', repayment_reduction: 'Reduce repayments', smsf: 'SMSF lending', other: 'Other' };

export const loanSummary = (app, applicants) => {
  const { loan } = householdPosition(app, applicants);
  return { amount: loan.amount || null, purpose: PURPOSE[loan.purpose] || app.objective_label || null, lvr: loan.lvr };
};

// One status per client-facing workstream: done | wait | hot (needs us now) | idle (not started).
export const workstreams = ({ applicants, documents, esign, idv, idvMeta, idvFor }) => {
  const secTotal = applicants.reduce((n, a) => n + a.sections.length, 0);
  const secDone = applicants.reduce((n, a) => n + a.sections.filter((x) => x.state === 'complete').length, 0);
  const portalDone = secTotal > 0 && secDone === secTotal;
  const portal = { key: 'portal', label: 'Client portal', tab: 'Fact find',
    tone: portalDone ? 'done' : secDone ? 'wait' : 'idle',
    value: portalDone ? 'Completed' : secDone ? `${secDone} of ${secTotal} sections` : 'Not started',
    sub: portalDone ? 'All sections confirmed' : 'Waiting on the client' };

  const live = esign.filter((e) => e.status !== 'voided');
  const signed = live.filter((e) => statusOf(e).key === 'completed').length;
  const bad = live.some((e) => ['declined', 'expired'].includes(statusOf(e).key));
  const es = { key: 'esign', label: 'E-Sign', tab: 'E-Sign',
    tone: !live.length ? 'idle' : bad ? 'hot' : signed === live.length ? 'done' : 'wait',
    value: !live.length ? 'Not sent' : signed === live.length ? 'Completed' : `${signed} of ${live.length} signed`,
    sub: !live.length ? 'No documents sent yet' : bad ? 'Declined or expired. Resend' : signed === live.length ? 'All documents signed' : 'Awaiting signatures' };

  const metas = applicants.map((a) => idvMeta(idvFor(idv, a.id)));
  const idDone = metas.filter((m) => m.label === 'Completed').length;
  const idHot = metas.some((m) => ['Requires review', 'Expired'].includes(m.label));
  const idSent = metas.some((m) => m.label !== 'Not sent');
  const id = { key: 'idv', label: 'ID verification', tab: 'ID Verification',
    tone: idDone === applicants.length && applicants.length ? 'done' : idHot ? 'hot' : idSent ? 'wait' : 'idle',
    value: idDone === applicants.length && applicants.length ? 'Completed' : idSent ? `${idDone} of ${applicants.length} verified` : 'Not requested',
    sub: idHot ? (metas.some((m) => m.label === 'Requires review') ? 'Submitted. Needs your review' : 'Link expired. Resend') : idDone === applicants.length && applicants.length ? 'All applicants verified' : idSent ? 'Waiting on the client' : 'Request when ready' };

  const req = documents.filter((x) => x.required);
  const accepted = req.filter((x) => !['requested', 'needs_replacement'].includes(x.status)).length;
  const toReview = documents.filter((x) => ['uploaded', 'processing', 'under_review'].includes(x.status)).length;
  const docs = { key: 'docs', label: 'Documents', tab: 'Documents',
    tone: toReview ? 'hot' : req.length && accepted === req.length ? 'done' : req.length ? 'wait' : 'idle',
    value: req.length ? `${accepted} of ${req.length} received` : 'None requested',
    sub: toReview ? `${toReview} uploaded. Review now` : req.length && accepted === req.length ? 'Everything received' : req.length ? `${req.length - accepted} outstanding from client` : 'Requested after employment is confirmed' };
  return [portal, es, id, docs];
};

// The single most useful next move for this file.
export const nextAction = ({ app, tasks, streams, canReady }) => {
  if (app.status === 'cancelled') return { text: 'Client portal cancelled', tone: 'idle' };
  if (['ready_for_assessment', 'exported'].includes(app.status)) return { text: 'With credit for assessment', tone: 'done' };
  const hot = streams.find((s) => s.tone === 'hot');
  if (hot) return { text: `${hot.label}: ${hot.sub}`, tone: 'hot', tab: hot.tab };
  const team = tasks.find((t) => t.owner === 'team');
  if (team) return { text: team.what, tone: 'hot', tab: 'Overview' };
  const client = tasks.filter((t) => t.owner === 'client');
  if (client.length) return { text: `Chase client: ${client.length} item${client.length === 1 ? '' : 's'} outstanding`, tone: 'wait', tab: 'Overview' };
  if (canReady) return { text: 'Move to Ready for Assessment', tone: 'hot', ready: true };
  return { text: 'Nothing outstanding', tone: 'done' };
};
